'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: 'All Topics' },
    { id: 'technical', label: 'Technical' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'platform', label: 'Platform' },
    { id: 'pricing', label: 'Pricing' }
  ];

  const faqs: FAQ[] = [
    {
      question: 'What manufacturing processes does Techaero support?',
      answer: 'Techaero provides comprehensive aerospace manufacturing capabilities including CNC machining (3-axis, 4-axis, 5-axis), additive manufacturing (SLS, SLA, DMLS), sheet metal fabrication, composite manufacturing, and precision assembly. All processes are AS9100 certified and ITAR compliant.',
      category: 'technical'
    },
    {
      question: 'How does Techaero ensure AS9100 compliance?',
      answer: 'Our facilities maintain AS9100 Rev D certification with rigorous quality management systems. We conduct regular internal audits, maintain comprehensive documentation, implement statistical process control, and provide full traceability for all aerospace components. Third-party audits verify our compliance annually.',
      category: 'compliance'
    },
    {
      question: 'What is the typical response time for technical support?',
      answer: 'Standard technical inquiries receive responses within 2 hours during business hours. Critical manufacturing issues are addressed immediately through our 24/7 emergency hotline. Complex technical consultations can be scheduled within 24 hours with our aerospace engineering specialists.',
      category: 'platform'
    },
    {
      question: 'How are manufacturing projects quoted and priced?',
      answer: 'Project pricing is based on material specifications, manufacturing complexity, volume requirements, quality standards, and timeline constraints. We provide detailed quotes within 48 hours including material costs, processing fees, quality assurance, and delivery. Volume discounts and partnership pricing available for ongoing projects.',
      category: 'pricing'
    },
    {
      question: 'What materials can Techaero process for aerospace applications?',
      answer: 'We process aerospace-grade materials including aluminum alloys (6061, 7075, 2024), titanium alloys (Ti-6Al-4V, Ti-6Al-2Sn-4Zr-2Mo), stainless steels (300 series, 400 series), Inconel, composites (carbon fiber, fiberglass), and engineering plastics (PEEK, Ultem). All materials are certified with full material traceability.',
      category: 'technical'
    },
    {
      question: 'Does Techaero handle ITAR-controlled projects?',
      answer: 'Yes, Techaero is ITAR registered and maintains secure facilities for defense-related manufacturing. Our team includes US persons with appropriate security clearances. We implement strict access controls, secure data management, and compliance protocols for all ITAR-controlled projects.',
      category: 'compliance'
    },
    {
      question: 'How can I track my manufacturing project in real-time?',
      answer: 'The Techaero Platform provides real-time project tracking with live manufacturing status updates, quality inspection reports, timeline milestones, and delivery tracking. Clients receive automated notifications for key project events and can access detailed progress reports through the secure portal.',
      category: 'platform'
    },
    {
      question: 'What quality documentation does Techaero provide?',
      answer: 'We provide comprehensive quality documentation including First Article Inspection Reports (FAIR), Material Certifications, Dimensional Inspection Reports, Non-Destructive Testing (NDT) results, Certificate of Conformance (C of C), and AS9102 documentation. All reports are digitally accessible through the client portal.',
      category: 'compliance'
    },
    {
      question: 'Can Techaero support rapid prototyping for aerospace projects?',
      answer: 'Yes, our additive manufacturing capabilities enable rapid prototyping with aerospace-grade materials. We can produce functional prototypes within 3-5 business days for design validation, fit checks, and performance testing. Expedited services available for critical timeline requirements.',
      category: 'technical'
    },
    {
      question: 'What is the minimum order quantity for aerospace components?',
      answer: 'Techaero supports projects from single prototype units to high-volume production runs. There is no minimum order quantity, making us ideal for both R&D prototyping and full-scale manufacturing. Pricing scales with volume, and we offer flexible production scheduling.',
      category: 'pricing'
    }
  ];

  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-card">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full mb-6">
            <Icon name="QuestionMarkCircleIcon" size={20} className="text-accent" />
            <span className="font-cta text-sm text-accent tracking-wider">FREQUENTLY ASKED</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Common Questions
            <span className="block text-accent mt-2">Answered by Experts</span>
          </h2>

          <p className="text-lg text-muted-foreground">
            Find quick answers to common aerospace manufacturing questions
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-2 rounded-lg font-cta text-sm transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-accent text-primary shadow-glow'
                  : 'bg-muted text-technical-gray hover:bg-accent/10 hover:text-accent'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFAQs.map((faq, index) => (
            <div
              key={index}
              className="bg-background border border-border rounded-lg overflow-hidden transition-all duration-300 hover:border-accent"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left group"
              >
                <span className="font-headline text-lg font-semibold text-foreground group-hover:text-accent transition-colors duration-300 pr-4">
                  {faq.question}
                </span>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  className={`text-accent flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-5 animate-slide-in-left">
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="mt-16 text-center p-8 bg-gradient-to-br from-accent/5 to-secondary/5 border border-accent/20 rounded-xl">
          <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-accent mx-auto mb-4" />
          <h3 className="font-headline text-2xl font-bold text-foreground mb-3">
            Still Have Questions?
          </h3>
          <p className="text-muted-foreground mb-6">
            Our aerospace manufacturing specialists are ready to help
          </p>
          <button className="px-8 py-3 bg-mission-orange text-primary font-cta text-sm font-medium rounded-lg transition-all duration-300 hover:shadow-glow hover:-translate-y-1">
            CONTACT SUPPORT TEAM
          </button>
        </div>
      </div>
    </section>
  );
}